import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { AppComponent } from './app/app.component';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { LOCALIZED_APP_ROUTES } from './app/app.routes';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
// Material 20 still needs the JS animation engine; animate.enter does not replace it.
import { provideAnimations } from '@angular/platform-browser/animations'; // NOSONAR
import { registerLocaleData } from '@angular/common';
import {
  Injectable,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import tippy from 'tippy.js';
import { NGX_TIPPY_CONFIG } from 'ngx-tippy-wrapper';
import {
  httpRequestInterceptor,
  TranslateRouterLinkPipe,
  LocalizeRouterService,
  provideLocalizeRouter,
  GTM_ID,
} from './app/core';
ModuleRegistry.registerModules([AllEnterpriseModule]);
provideGlobalGridOptions({ theme: 'legacy', columnMenu: 'legacy' });
// MatDialog uses the Popover API (top layer). A tooltip on document.body
// stays behind that layer no matter the z-index. Mount inside the open dialog.
const tippyDefaults = {
  appendTo: (reference: Element) =>
    reference.closest('.cdk-overlay-popover') ?? document.body,
  zIndex: 10050,
};
tippy.setDefaultProps(tippyDefaults);
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-053651}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Traffic_Tech}_is_granted_a_{Multiple_Applications}_Developer_License_for_{5}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{23_March_2025}____[v3]_[01]_MTc0MjY4ODAwMDAwMA==599fb4422b244f44066ce6f7518bd54d'
);
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import { CanFindLocalizedRouteGuard } from './app/can-find-localized-route.guard';
import { environment } from './environments/environment';
import { MatDialogModule } from '@angular/material/dialog';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { provideEnvironmentNgxMask } from 'ngx-mask';

@Injectable()
class KeepMotionMediaMatcher extends MediaMatcher {
  override matchMedia(query: string): MediaQueryList {
    // Material 20 treats Windows "Animation effects" off as disable-all.
    // Angular 15 ignored that OS flag; keep motion on for those PCs.
    if (query.includes('prefers-reduced-motion')) {
      return super.matchMedia('not all');
    }
    return super.matchMedia(query);
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      LOCALIZED_APP_ROUTES,
      withPreloading(PreloadAllModules),
      // Angular 22 defaults this to 'always'. Keep 21's emptyOnly so localized
      // parent :lang params do not leak into child routes.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })
    ),
    provideHttpClient(withXhr(), withInterceptors([httpRequestInterceptor])),
    provideAnimations(), // NOSONAR Material still requires the animation engine
    { provide: MediaMatcher, useClass: KeepMotionMediaMatcher },

    TranslateRouterLinkPipe,
    provideLocalizeRouter(),
    provideFlatpickrDefaults(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    {
      provide: LOCALE_ID,
      deps: [LocalizeRouterService],
      useFactory: GlobalLocaleFactory,
    },
    { provide: GTM_ID, useValue: environment.gtm },
    CanFindLocalizedRouteGuard,
    importProvidersFrom(MatDialogModule),
    // Dialogs use the browser top layer by default, which covers the reCAPTCHA
    // challenge. Keep overlays in normal stacking so that challenge can open.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    {
      provide: NGX_TIPPY_CONFIG,
      useValue: tippyDefaults,
    },
    importProvidersFrom(NgIdleKeepaliveModule.forRoot()),
    provideEnvironmentNgxMask(),
  ],
}).catch(err => console.error(err));

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
export function GlobalLocaleFactory(localizeService: any) {
  registerLocaleData(localeFr);
  registerLocaleData(localeEs);

  return localizeService.parser.currentLang;
}
